// WO-21: helpers for singles of games served by /api/products (GameCardPage).

export const humanize = (key) => String(key || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// Printings of the page: the catalog's variants (finish + display_name, in catalog order), plus any finish that only
// appears in prices or listings (labelled from the price row's rarity).
export function printingsOf(product) {
  const out = [];
  const seen = new Set();
  (product?.variants || []).forEach((v) => {
    if (!v?.finish || seen.has(v.finish)) return;
    seen.add(v.finish);
    out.push({ finish: v.finish, label: v.display_name || humanize(v.finish), isDefault: !!v.is_default });
  });
  (product?.prices || []).forEach((p) => {
    if (!p?.finish || seen.has(p.finish)) return;
    seen.add(p.finish);
    out.push({ finish: p.finish, label: p.rarity || humanize(p.finish), isDefault: false });
  });
  return out;
}

// The printing the chart opens on: the catalog default, else the printing of the headline price, else the first.
export function defaultPrintingOf(product, printings) {
  const byDefault = printings.find((p) => p.isDefault);
  if (byDefault) return byDefault.finish;
  const primary = product?.primary_price || product?.tcgplayer_price;
  if (primary) {
    const row = (product?.prices || []).find((p) => p.price === primary.price && (!primary.rarity || p.rarity === primary.rarity));
    if (row?.finish) return row.finish;
  }
  return printings[0]?.finish || null;
}

// WO-35: what each game's players look for first. `stats` builds the one-line stat summary under the name,
// `chips` the badges next to it, `order` the order of the Card Details rows (keys not listed follow, humanised),
// `labels` the row names. Empty values are never shown. Internal catalog keys (ids, URLs, backup bookkeeping) and
// keys shown elsewhere on the page (rarity, card text, flavor text) are hidden.
const HIDDEN_ATTRS = new Set(['text', 'flavor_text', 'rarity', 'rarities', 'rarities_not_in_backup', 'backup_id',
  'backup_name', 'tcgplayer_url', 'tcgplayer_name', 'tcgplayer_id', 'source_image', 'tcgcsv_group', 'image_url']);

const present = (v) => v !== undefined && v !== null && String(v).trim() !== '' && !(Array.isArray(v) && !v.length);
const stat = (label, v, fmt = (x) => x) => (present(v) ? `${label} ${fmt(v)}` : null);

export const GAME_LAYOUTS = {
  yugioh: {
    stats: (a) => [stat('Level', a.level), stat('Rank', a.rank), stat('Link', a.link_rating),
      stat('ATK', a.atk), stat('DEF', a.def)],
    chips: ['card_type', 'attribute', 'monster_type'],
    order: ['card_type', 'attribute', 'monster_type', 'level', 'rank', 'link_rating', 'link_arrows',
      'pendulum_scale', 'atk', 'def', 'passcode'],
    labels: { monster_type: 'Type', link_rating: 'Link rating', link_arrows: 'Link arrows',
      pendulum_scale: 'Pendulum scale', atk: 'ATK', def: 'DEF' },
  },
  mtg: {
    stats: (a) => [stat('Mana cost', a.mana_cost), present(a.power) || present(a.toughness)
      ? `${a.power ?? '?'}/${a.toughness ?? '?'}` : null, stat('Loyalty', a.loyalty)],
    chips: ['type_line'],
    order: ['mana_cost', 'type_line', 'power', 'toughness', 'loyalty', 'colors', 'finishes', 'collector_number'],
    labels: { type_line: 'Type', mana_cost: 'Mana cost' },
  },
  lorcana: {
    stats: (a) => [stat('Cost', a.cost), stat('Strength', a.strength), stat('Willpower', a.willpower),
      stat('Lore', a.lore), stat('Move cost', a.move_cost)],
    chips: ['ink', 'card_type', 'inkwell'],
    order: ['card_type', 'ink', 'cost', 'inkwell', 'strength', 'willpower', 'lore', 'move_cost', 'classification',
      'version', 'franchise'],
    labels: { inkwell: 'Inkable', classification: 'Classifications', move_cost: 'Move cost' },
    chipText: { inkwell: (v) => (/^(yes|true)$/i.test(String(v)) ? 'Inkable' : null) },
  },
  onepiece: {
    stats: (a) => [stat('Cost', a.cost), stat('Life', a.life), stat('Power', a.power),
      stat('Counter', a.counter, (v) => (String(v).startsWith('+') || String(v) === '-' ? v : `+${v}`))],
    chips: ['color', 'card_type', 'attribute'],
    order: ['card_type', 'color', 'cost', 'life', 'power', 'counter', 'attribute', 'subtypes'],
    labels: { subtypes: 'Types' },
  },
  swu: {
    stats: (a) => [stat('Cost', a.cost), stat('Power', a.power), stat('HP', a.hp)],
    chips: ['aspect', 'card_type', 'arena'],
    order: ['card_type', 'aspect', 'cost', 'power', 'hp', 'arena', 'traits', 'epic_action'],
    labels: { hp: 'HP', epic_action: 'Epic action' },
  },
};

// MTG's catalog stores rarity as a one-letter code.
const MTG_RARITY = { C: 'Common', U: 'Uncommon', R: 'Rare', M: 'Mythic Rare', S: 'Special', P: 'Promo', T: 'Token', L: 'Land' };
export const rarityLabel = (gameId, rarity) => (gameId === 'mtg' && MTG_RARITY[rarity]) || rarity;

// "Force;Imperial;Sith" / ["a","b"] -> "Force, Imperial, Sith"; objects are not shown.
export function attrText(v) {
  if (Array.isArray(v)) return v.filter(present).join(', ');
  if (v !== null && typeof v === 'object') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v).split(';').map((s) => s.trim()).filter(Boolean).join(', ');
}

// Rows for Card Details: [[label, value], ...] in the game's order, empty and internal keys left out.
export function attributeRows(gameId, attrs) {
  const layout = GAME_LAYOUTS[gameId] || {};
  const order = layout.order || [];
  const labels = layout.labels || {};
  const a = attrs || {};
  const keys = [...order.filter((k) => k in a), ...Object.keys(a).filter((k) => !order.includes(k))]
    .filter((k) => !HIDDEN_ATTRS.has(k) && present(a[k]));
  return keys.map((k) => [labels[k] || humanize(k), attrText(a[k])]).filter(([, v]) => v);
}

export function statLine(gameId, attrs) {
  const layout = GAME_LAYOUTS[gameId];
  return layout ? layout.stats(attrs || {}).filter(Boolean).join(' · ') : '';
}

export function headerChips(gameId, attrs) {
  const layout = GAME_LAYOUTS[gameId];
  if (!layout) return [];
  const a = attrs || {};
  return layout.chips
    .map((k) => (present(a[k]) ? (layout.chipText?.[k] ? layout.chipText[k](a[k]) : attrText(a[k])) : null))
    .filter(Boolean);
}

// Card number for the facts table: "208/204" stays as printed; a bare "161" gets the set's printed total.
export function cardNumberLabel(number, printedTotal) {
  if (!present(number)) return null;
  const n = String(number);
  return printedTotal && !n.includes('/') && !n.includes('-') ? `${n}/${printedTotal}` : n;
}

// A price row's edition, only when the row really has one (Yu-Gi-Oh 1st Edition / Unlimited). Other games' rows
// carry their printing in edition_name ("Normal", "Holofoil"), which is not an edition.
const EDITION_NAMES = { '1st_edition': '1st Edition', unlimited: 'Unlimited', limited_edition: 'Limited Edition' };
export const editionLabel = (p) => (p?.edition ? (p.edition_name || EDITION_NAMES[p.edition] || humanize(p.edition)) : '');
